import * as React from 'react';
import { useSelector } from 'react-redux';
import { artikelGruppenByNrSelector, einheitenByCodeSelector } from 'store/lookups/selector';
import { AuftragPosition } from 'api/auftragPosition';
import classnames from 'classnames';
import { formatDecimal } from 'utils/formatter';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { LieferArt } from 'formComponents/lookups/LieferArt';
import { formStyles } from 'styles/formStyles';
import { FormDecimalField } from 'formComponents/FormDecimalField';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';

interface AuftragPositionenProps {
    auftragPositionen: AuftragPosition[];
    onChange: (newTransferPositionen: AuftragPosition[]) => void;
    withBestand: boolean;
    withLArt: boolean;
    trProzCalc?: number;
}

const styles = mergeStyleSets({
    trProzValue: {
        fontStyle: 'italic',
    },
});

export const AuftragPositionenSelect: React.FC<AuftragPositionenProps> = ({
    auftragPositionen,
    onChange,
    withBestand,
    withLArt,
    trProzCalc,
}) => {
    const artikelGruppenByNr = useSelector(artikelGruppenByNrSelector);
    const einheitenByCode = useSelector(einheitenByCodeSelector);

    return (
        <div>
            <h3>Bitte wählen Sie die zu übernehmenden Positionen und Mengen</h3>
            <table className={formStyles.positionenTable}>
                <thead className={formStyles.positionenTable}>
                    <tr>
                        <td />
                        <td className={formStyles.positionenNrCell}>Pos.</td>
                        <td className={formStyles.positionenArtikelGruppeCell}>Art. Grp.</td>
                        <td className={formStyles.positionenArtikelNrCell}>Art. Nr.</td>
                        <td className={formStyles.positionenTextCell}>Text</td>
                        <td className={formStyles.positionenEinhCell}>Einh</td>
                        <td className={formStyles.positionenMoneyCell}>Menge</td>
                        <td className={formStyles.positionenMoneyCell}>Offen</td>
                        <td className={formStyles.positionenMoneyCell}>VK-Netto</td>
                        <td className={formStyles.positionenMoneyCell}>VK-Brutto</td>
                        {withBestand && <td className={formStyles.positionenMoneyCell}>Bestand</td>}
                        <td className={formStyles.transferMengeHeader}>
                            Menge
                            <br />
                            übernehmen
                        </td>
                        {withLArt && <td className={formStyles.transferLArt}>LArt</td>}
                    </tr>
                </thead>
                <tbody>
                    {auftragPositionen.map((auftragPosition) => (
                        <tr key={auftragPosition.pos} className={formStyles.positionenRow}>
                            <td>
                                <Checkbox
                                    className={formStyles.checkbox}
                                    checked={auftragPosition.transfer}
                                    onChange={(event, checked) => {
                                        onChange(
                                            auftragPositionen.map((ap) =>
                                                ap.pos === auftragPosition.pos
                                                    ? {
                                                          ...auftragPosition,
                                                          transfer: checked,
                                                      }
                                                    : ap,
                                            ),
                                        );
                                    }}
                                />
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenNrCell)}>
                                {auftragPosition.pos}
                            </td>
                            <td
                                className={classnames(
                                    formStyles.positionenCell,
                                    formStyles.positionenArtikelGruppeCell,
                                )}
                            >
                                {(artikelGruppenByNr[auftragPosition.artikelGruppeNr] &&
                                    artikelGruppenByNr[auftragPosition.artikelGruppeNr]?.text) ||
                                    auftragPosition.artikelGruppeNr}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenArtikelNrCell)}>
                                <span>{auftragPosition.artikelNr}</span>
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenTextCell)}>
                                {auftragPosition.posText}
                                {auftragPosition.posBem ? (
                                    <i>
                                        <br />
                                        {auftragPosition.posBem}
                                    </i>
                                ) : (
                                    ''
                                )}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenEinhCell)}>
                                {auftragPosition.posEinh
                                    ? einheitenByCode[auftragPosition.posEinh].text || auftragPosition.posEinh
                                    : '?'}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenMengeCell)}>
                                {formatDecimal(auftragPosition.posMenge, 3)}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}>
                                {formatDecimal(auftragPosition.offen, 3)}
                            </td>
                            <td
                                className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell, {
                                    [styles.trProzValue]: !!trProzCalc,
                                })}
                            >
                                {formatDecimal(
                                    trProzCalc
                                        ? (auftragPosition.posNetto * trProzCalc) / 100
                                        : auftragPosition.posNetto,
                                    2,
                                )}
                            </td>
                            <td
                                className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell, {
                                    [styles.trProzValue]: !!trProzCalc,
                                })}
                            >
                                {formatDecimal(
                                    trProzCalc
                                        ? (auftragPosition.posBrutto * trProzCalc) / 100
                                        : auftragPosition.posBrutto,
                                    2,
                                )}
                            </td>
                            {withBestand && (
                                <td className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}>
                                    {auftragPosition.transferBestand !== undefined
                                        ? formatDecimal(auftragPosition.transferBestand, 3)
                                        : ''}
                                </td>
                            )}
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}>
                                <FormDecimalField
                                    options={{
                                        disabled: !auftragPosition.transfer,
                                        fixed: 3,
                                    }}
                                    label=""
                                    value={auftragPosition.transferMenge || 0}
                                    onChange={(newValue): void => {
                                        onChange(
                                            auftragPositionen.map((ap) =>
                                                ap.pos === auftragPosition.pos
                                                    ? {
                                                          ...auftragPosition,
                                                          transferMenge: newValue,
                                                      }
                                                    : ap,
                                            ),
                                        );
                                    }}
                                />
                            </td>
                            {withLArt && (
                                <td className={classnames(formStyles.positionenCell, formStyles.transferLArt)}>
                                    <LieferArt
                                        allowEmpty
                                        disabled={!auftragPosition.transfer}
                                        value={(auftragPosition?.transferLArt as string) || ''}
                                        menuPlacement="top"
                                        renderLabel="no"
                                        onChange={(newValue) => {
                                            onChange(
                                                auftragPositionen.map((ap) =>
                                                    ap.pos === auftragPosition.pos
                                                        ? {
                                                              ...auftragPosition,
                                                              transferLArt: newValue,
                                                          }
                                                        : ap,
                                                ),
                                            );
                                        }}
                                    />
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
